/** @format */

/**
 * Shared sanitizers for accounting-related item lists.
 * Each helper mirrors the previous in-component logic to keep behaviour unchanged.
 */
import {
  parseNumberSafe,
  parseRateSafe,
  normalizeDateInput,
} from "./accounting";
import type {
  OtherOrderItem,
  PaymentVoucherItem,
  PaymentBankingItem,
  ReceiptBankingItem,
  ReceiptItem,
  PurchaseOrderItem,
  SaleOrderItem,
  ReturnOrderItem,
} from "types";

const getUnitLabel = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (typeof value === "object") {
    return value.name ?? value.code ?? "";
  }
  return "";
};

export const sanitizeOtherOrderItems = (
  items?: OtherOrderItem[],
  customerMap?: Map<number | string, any>,
) =>
  Array.isArray(items)
    ? items
        .map((item) => {
          const amount = parseNumberSafe(item.amount);
          const vatRate = parseRateSafe(item.vat_rate);
          const vatReduceRate = parseRateSafe(item.vat_reduce_rate);
          const pitRate = parseRateSafe(item.pit_rate);
          const objectId =
            item.object_id !== undefined &&
            item.object_id !== null &&
            String(item.object_id).trim() !== ""
              ? Number.isNaN(Number(item.object_id))
                ? String(item.object_id).trim()
                : Number(item.object_id)
              : undefined;

          const matched =
            objectId !== undefined
              ? customerMap?.get(objectId) || customerMap?.get(String(objectId))
              : undefined;

          const operationName =
            item.operation_name && String(item.operation_name).trim()
              ? String(item.operation_name).trim()
              : undefined;
          const objectCode =
            item.object_code && String(item.object_code).trim()
              ? String(item.object_code).trim()
              : matched?.code;
          const objectName =
            item.object_name && String(item.object_name).trim()
              ? String(item.object_name).trim()
              : matched?.name;
          const reason = item.reason ? String(item.reason).trim() : undefined;
          const description = item.description
            ? String(item.description).trim()
            : undefined;

          return {
            ...(operationName ? { operation_name: operationName } : {}),
            amount,
            ...(vatRate !== undefined ? { vat_rate: vatRate } : {}),
            ...(vatReduceRate !== undefined
              ? { vat_reduce_rate: vatReduceRate }
              : {}),
            ...(pitRate !== undefined ? { pit_rate: pitRate } : {}),
            ...(objectId !== undefined ? { object_id: objectId } : {}),
            ...(objectCode ? { object_code: objectCode } : {}),
            ...(objectName ? { object_name: objectName } : {}),
            ...(reason ? { reason } : {}),
            ...(description ? { description } : {}),
          };
        })
        .filter(
          (item) =>
            parseNumberSafe(item.amount ?? 0) > 0 ||
            Boolean(item.operation_name) ||
            Boolean(item.object_id) ||
            Boolean(item.object_code) ||
            Boolean(item.object_name) ||
            Boolean(item.reason) ||
            Boolean(item.description) ||
            item.vat_rate !== undefined ||
            item.vat_reduce_rate !== undefined ||
            item.pit_rate !== undefined,
        )
    : [];

export const sanitizePaymentVoucherItems = (
  items?: PaymentVoucherItem[],
): PaymentVoucherItem[] =>
  Array.isArray(items)
    ? items
        .map((item) => {
          const normalizedAmount = parseNumberSafe(item.amount);
          const partnerIdValue =
            item.partner_id !== undefined &&
            item.partner_id !== null &&
            String(item.partner_id).trim() !== "" &&
            Number.isFinite(Number(item.partner_id))
              ? Number(item.partner_id)
              : undefined;

          return {
            amount: normalizedAmount,
            ...(partnerIdValue !== undefined
              ? { partner_id: partnerIdValue }
              : {}),
            reason: item.reason ? String(item.reason).trim() : undefined,
            description: item.description
              ? String(item.description).trim()
              : undefined,
          };
        })
        .filter(
          (item) =>
            item.amount ||
            item.partner_id ||
            Boolean(item.reason) ||
            Boolean(item.description),
        )
    : [];

export const sanitizePaymentBankingItems = (
  items?: PaymentBankingItem[],
): PaymentBankingItem[] =>
  Array.isArray(items)
    ? items
        .map((item) => ({
          amount: parseNumberSafe(item.amount),
          partner_id:
            item.partner_id !== undefined && item.partner_id !== null
              ? Number.isFinite(Number(item.partner_id))
                ? Number(item.partner_id)
                : undefined
              : undefined,
          expense_reason_id:
            item.expense_reason_id !== undefined &&
            item.expense_reason_id !== null
              ? Number.isFinite(Number(item.expense_reason_id))
                ? Number(item.expense_reason_id)
                : undefined
              : undefined,
          description: item.description
            ? String(item.description).trim()
            : undefined,
        }))
        .filter(
          (item) =>
            item.amount ||
            item.partner_id ||
            item.expense_reason_id ||
            Boolean(item.description),
        )
    : [];

export const sanitizeReceiptBankingItems = (
  items?: ReceiptBankingItem[],
): ReceiptBankingItem[] =>
  Array.isArray(items)
    ? items
        .map((item) => ({
          amount: parseNumberSafe(item.amount),
          partner_id:
            item.partner_id !== undefined && item.partner_id !== null
              ? Number.isFinite(Number(item.partner_id))
                ? Number(item.partner_id)
                : String(item.partner_id).trim()
              : undefined,
          reason: item.reason ? String(item.reason).trim() : undefined,
          business_field: item.business_field
            ? String(item.business_field).trim()
            : undefined,
          description: item.description
            ? String(item.description).trim()
            : undefined,
          vat_rate: parseRateSafe(item.vat_rate),
          vat_amount: parseNumberSafe(item.vat_amount),
          pit_rate: parseRateSafe(item.pit_rate),
          pit_amount: parseNumberSafe(item.pit_amount),
          net_amount: parseNumberSafe(item.net_amount),
        }))
        .filter(
          (item) =>
            item.amount ||
            item.partner_id ||
            item.reason ||
            item.description ||
            item.vat_rate !== undefined ||
            item.pit_rate !== undefined,
        )
    : [];

export const sanitizeReceiptItems = (items?: ReceiptItem[]): ReceiptItem[] =>
  Array.isArray(items)
    ? items
        .map((item) => {
          const normalizedAmount = parseNumberSafe(item.amount);
          const partnerIdValue =
            item.partner_id !== undefined &&
            item.partner_id !== null &&
            String(item.partner_id).trim() !== "" &&
            Number.isFinite(Number(item.partner_id))
              ? Number(item.partner_id)
              : undefined;

          const vatRate = parseRateSafe(item.vat_rate);
          const pitRate = parseRateSafe(item.pit_rate);

          return {
            amount: normalizedAmount,
            ...(partnerIdValue !== undefined
              ? { partner_id: partnerIdValue }
              : {}),
            reason: item.reason ? String(item.reason).trim() : undefined,
            vat_rate: vatRate,
            pit_rate: pitRate,
          };
        })
        .filter(
          (item) =>
            item.amount ||
            item.partner_id ||
            Boolean(item.reason) ||
            item.vat_rate ||
            item.pit_rate,
        )
    : [];

export const sanitizePurchaseOrderItems = (
  items?: PurchaseOrderItem[],
  wmsMap?: Map<number | string, any>,
) =>
  Array.isArray(items)
    ? items
        .map((item) => {
          const quantity = parseNumberSafe(item.quantity);
          const unitPrice = parseNumberSafe(item.unit_price);
          const amount = quantity * unitPrice;
          const productId =
            item.product_id !== undefined && item.product_id !== null
              ? Number.isFinite(Number(item.product_id))
                ? Number(item.product_id)
                : (item as any).product_id
              : undefined;
          const selected =
            wmsMap?.get(productId as any) ||
            wmsMap?.get(String(productId ?? "")) ||
            wmsMap?.get(item.unit_id as any) ||
            wmsMap?.get(String(item.unit_id ?? ""));
          const vatRate = parseRateSafe(item.vat_rate);
          const vatAmount =
            vatRate !== undefined && Number.isFinite(vatRate)
              ? (amount * vatRate) / 100
              : undefined;

          return {
            ...(productId !== undefined ? { product_id: productId } : {}),
            product_name:
              item.product_name && String(item.product_name).trim()
                ? String(item.product_name).trim()
                : selected?.name
                  ? String(selected.name)
                  : undefined,
            ...(item.unit_id !== undefined && item.unit_id !== null
              ? { unit_id: item.unit_id }
              : selected?.unit_id !== undefined && selected?.unit_id !== null
                ? { unit_id: selected.unit_id }
                : {}),
            unit_label:
              (item as any).unit_label ||
              getUnitLabel((selected as any)?.unit) ||
              (selected?.unit_id ? String(selected.unit_id) : undefined),
            quantity,
            unit_price: unitPrice,
            amount,
            vat_rate: vatRate,
            ...(vatAmount !== undefined ? { vat_amount: vatAmount } : {}),
            description: item.description
              ? String(item.description).trim()
              : undefined,
          };
        })
        .filter(
          (item) =>
            Number(item.quantity ?? 0) > 0 ||
            Number(item.unit_price ?? 0) > 0 ||
            Boolean(item.product_id) ||
            Boolean(item.product_name) ||
            Boolean(item.description),
        )
    : [];

export const sanitizeSaleOrderItems = (items?: SaleOrderItem[]) =>
  Array.isArray(items)
    ? items
        .map((item) => {
          const quantity = parseNumberSafe(item.quantity);
          const price = parseNumberSafe(item.price);
          const baseAmount = quantity * price;
          const discountRate =
            item.discount_rate !== undefined && item.discount_rate !== null
              ? Number(item.discount_rate)
              : undefined;
          const discountAmount =
            discountRate !== undefined ? (baseAmount * discountRate) / 100 : 0;
          const otherReduction =
            item.other_reduction !== undefined && item.other_reduction !== null
              ? parseNumberSafe(item.other_reduction)
              : 0;
          const amount =
            (item as any).amount !== undefined && item.amount !== null
              ? parseNumberSafe((item as any).amount)
              : Math.max(baseAmount - discountAmount - otherReduction, 0);
          const vatRate = parseRateSafe(item.vat_rate);
          const vatAmount =
            item.vat_amount !== undefined && item.vat_amount !== null
              ? parseNumberSafe(item.vat_amount)
              : vatRate !== undefined
                ? (amount * vatRate) / 100
                : 0;

          return {
            ...(item.id ? { id: item.id } : {}),
            product_id: Number.isFinite(Number(item.product_id))
              ? Number(item.product_id)
              : (item as any).product_id,
            wms_id:
              item.wms_id !== undefined && item.wms_id !== null
                ? Number(item.wms_id)
                : undefined,
            unit_id:
              item.unit_id !== undefined && item.unit_id !== null
                ? Number(item.unit_id)
                : (item as any)?.unit?.id !== undefined &&
                    (item as any)?.unit?.id !== null
                  ? Number((item as any).unit.id)
                  : undefined,
            product_name: item.product_name
              ? String(item.product_name).trim()
              : undefined,
            unit: getUnitLabel((item as any)?.unit) || undefined,
            quantity,
            price,
            amount,
            discount_rate: discountRate,
            other_reduction:
              item.other_reduction !== undefined || discountRate !== undefined
                ? parseNumberSafe(item.other_reduction ?? 0)
                : undefined,
            vat_rate: vatRate,
            vat_amount: vatAmount,
            converted_unit: item.converted_unit,
            conversion_type: item.conversion_type,
            conversion_rate:
              item.conversion_rate !== undefined
                ? parseNumberSafe(item.conversion_rate)
                : undefined,
            converted_quantity:
              item.converted_quantity !== undefined
                ? parseNumberSafe(item.converted_quantity)
                : undefined,
            converted_price:
              item.converted_price !== undefined
                ? parseNumberSafe(item.converted_price)
                : undefined,
          };
        })
        .filter(
          (item) =>
            Number(item.quantity ?? 0) > 0 &&
            Number(item.price ?? 0) >= 0 &&
            Boolean(item.product_id),
        )
    : [];

export const sanitizeReturnOrderItems = (items?: ReturnOrderItem[]) =>
  Array.isArray(items)
    ? items
        .map((item) => {
          const wmsId =
            item.wms_id !== undefined && item.wms_id !== null
              ? Number(item.wms_id)
              : undefined;
          const categoryId =
            item.category_id !== undefined && item.category_id !== null
              ? Number(item.category_id)
              : undefined;
          const productId =
            item.product_id !== undefined && item.product_id !== null
              ? Number(item.product_id)
              : undefined;
          const unitId =
            item.unit_id !== undefined && item.unit_id !== null
              ? parseNumberSafe(item.unit_id)
              : undefined;
          const quantity = parseNumberSafe(item.quantity);
          const returnPrice = parseNumberSafe(
            (item as any).return_price ?? (item as any).return_unit_price,
          );
          const returnAmount =
            item.return_amount !== undefined && item.return_amount !== null
              ? parseNumberSafe(item.return_amount)
              : quantity * returnPrice || 0;
          const saleUnitPrice = parseNumberSafe(
            (item as any).sale_unit_price ?? 0,
          );
          const saleAmount =
            (item as any).sale_amount !== undefined &&
            (item as any).sale_amount !== null
              ? parseNumberSafe((item as any).sale_amount)
              : quantity * saleUnitPrice || 0;
          const convertQuantity = parseNumberSafe(
            (item as any).convert_quantity ?? 0,
          );
          const convertUnitPrice = parseNumberSafe(
            (item as any).convert_unit_price ?? 0,
          );
          const convertReturnPrice =
            (item as any).convert_return_price !== undefined &&
            (item as any).convert_return_price !== null
              ? parseNumberSafe((item as any).convert_return_price)
              : convertQuantity * convertUnitPrice || 0;
          const convertRatio =
            (item as any).convert_ratio !== undefined &&
            (item as any).convert_ratio !== null
              ? parseNumberSafe((item as any).convert_ratio)
              : undefined;

          return {
            wms_id: wmsId,
            category_id: categoryId,
            product_id: productId,
            product_name: item.product_name
              ? String(item.product_name).trim()
              : undefined,
            unit_id: unitId,
            quantity,
            return_price: returnPrice,
            return_amount: returnAmount,
            sale_unit_price: saleUnitPrice,
            sale_amount: saleAmount,
            convert_unit_id:
              (item as any).convert_unit_id !== undefined &&
              (item as any).convert_unit_id !== null
                ? parseNumberSafe((item as any).convert_unit_id)
                : undefined,
            convert_operator:
              (item as any).convert_operator !== undefined &&
              (item as any).convert_operator !== null
                ? String((item as any).convert_operator).trim()
                : undefined,
            convert_quantity: convertQuantity || undefined,
            convert_unit_price: convertUnitPrice || undefined,
            convert_return_price: convertReturnPrice || undefined,
            convert_ratio: convertRatio,
            description:
              (item as any).description !== undefined &&
              (item as any).description !== null
                ? String((item as any).description).trim()
                : undefined,
          };
        })
        .filter(
          (item) =>
            item.product_id ||
            item.product_name ||
            item.quantity ||
            item.return_price ||
            item.return_amount,
        )
    : [];

export const normalizeFormDate = (value?: string | null) =>
  normalizeDateInput(value);
